import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import {
    ButtonContainer,
    ButtonContiner,
    ErrorMessage,
    SuccessMessage,
} from 'src/components/FreeBetsForm/FreeBetsForm.style';
import { PaymentAdditionsNew } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/PaymentsAdditionsNew';
import { PaymentMethodDropdown } from 'src/components/PaymentMethodDropdown/PaymentMethodDropdown';
import { PaymentMethodDropdownState } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/PaymentMethodDropdown.state';
import { WithdrawFormState } from 'src/components/WithdrawForm/realex/WithdrawForm.state';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { YaspaState } from 'src/components/WithdrawForm/yaspa/YaspaSegment.state';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { PaymentProvidersType } from 'src/AppState/ConfigState/types';

interface WithdrawFormPropsType {
    permisionsAllowed: boolean;
    customerId: number;
    submitCallback: () => void;
    yaspaState: YaspaState;
    // Lists saved cards for this provider instead of the universe's deposit provider.
    paymentProvider?: PaymentProvidersType;
}

export const WithdrawForm = observer(
    'WithdrawForm',
    ({ permisionsAllowed, customerId, yaspaState, submitCallback, paymentProvider }: WithdrawFormPropsType) => {
        const { sdkStaff, models, common } = useAppStateContext();
        const configState = ConfigState.get(common);
        const {
            config: { usePaymentFormForUniverse, failedWithdrawalsFundsEnabled, minWithdrawAmount },
        } = configState;
        const cardProvider = paymentProvider ?? usePaymentFormForUniverse;

        const [paymentMethodDropdownStateAutoMap] = useState(
            new AutoMap((customerId: number) => new PaymentMethodDropdownState(models, customerId, cardProvider))
        );
        const paymentMethodDropdownState = paymentMethodDropdownStateAutoMap.get(customerId);

        const [stateAutoMap] = useState(
            new AutoMap(
                (customerId: number) =>
                    new WithdrawFormState(
                        paymentMethodDropdownState,
                        sdkStaff,
                        common.session,
                        permisionsAllowed,
                        models,
                        customerId,
                        submitCallback,
                        yaspaState,
                        minWithdrawAmount,
                        common.trpcClient
                    )
            )
        );
        const state = stateAutoMap.get(customerId);

        return (
            <>
                <Input
                    dataTest='withdrawal-amount-input'
                    formInputState={state.amount}
                    onChange={state.onChange}
                    label='Amount'
                    placeholder='Amount'
                />

                <ButtonContainer>
                    <PaymentAdditionsNew
                        value={state.amount.value}
                        currency={state.getCurrency}
                        onChange={state.handleSetAmount}
                        handleActionOnInput={state.handleSetMaximum}
                        actionButtonLabel='All'
                    />
                </ButtonContainer>

                <PaymentMethodDropdown
                    value={paymentMethodDropdownState.paymentMethodsValue}
                    onChange={paymentMethodDropdownState.handlePaymentSelectChange}
                    onValue={paymentMethodDropdownState.handlePaymentSelectChange}
                    customerId={customerId}
                    paymentProvider={cardProvider}
                />

                {state.successMessage === null ? null : (
                    <SuccessMessage data-test='success-message'>{state.successMessage}</SuccessMessage>
                )}
                {state.errorMessage === null ? null : <ErrorMessage>{state.errorMessage}</ErrorMessage>}
                {yaspaState.errors.length === 0
                    ? null
                    : yaspaState.errors.map((error) => <ErrorMessage key={error.type}>{error.message}</ErrorMessage>)}
                {permisionsAllowed === true ? null : (
                    <ErrorMessage> You don't have enough permissions to edit this</ErrorMessage>
                )}

                <table className='freebet-adjustment-form__summary'>
                    <tbody>
                        <tr data-test='withdraw-form-current-balance'>
                            <th>Current balance:</th>
                            <td>{state.getBalance.format(state.getCurrency)}</td>
                        </tr>
                        <tr data-test='withdraw-form-withdrawable-balance'>
                            <th>Withdrawable balance:</th>
                            <td>{state.getWithdrawableBalance.format(state.getCurrency)}</td>
                        </tr>
                        {failedWithdrawalsFundsEnabled ? (
                            <tr data-test='withdraw-form-failed-withdrawals-funds'>
                                <th>Failed withdrawals funds:</th>
                                <td>{state.getFailedWithdrawalsFunds.format(state.getCurrency)}</td>
                            </tr>
                        ) : null}
                        <tr data-test='withdraw-form-new-balance'>
                            <th>New balance:</th>
                            <td>{state.newBalance.format(state.getCurrency)}</td>
                        </tr>
                    </tbody>
                </table>
                <ButtonContiner>
                    <button
                        data-test='withdraw-form-submit-button'
                        className='btn'
                        type='submit'
                        value='clear'
                        disabled={state.isButtonDisabled}
                        onClick={state.handleSubmit}
                    >
                        Submit
                    </button>
                </ButtonContiner>
            </>
        );
    }
);
